import React, {
  KeyboardEvent as KeyboardSyntheticEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { cx } from '@emotion/css';

import { DISTANCE_UNIT } from '../../../../constants/ecp-locator';
import { useStyles } from './ecp-store.styles';
import { IEcpStoreProps } from './ecp-store.models';
import { useStoreByType } from '../../../../hooks/use-store-by-type';
import { Icon, IconSizes, IconTypes } from '../../../common';
import { isVendingMachine, roundRating } from '../../../../utils/ecp-locator';
import { getRoundDistance } from '../../../../utils/distance';
import { formatPhoneNumber } from '../../../../utils';
import { isYMEnabled, pushStoreCode } from '../../../../utils';

export const EcpStore = ({
  storeData,
  onStoreSelect,
  storeCode,
  onScrollToStore,
  contentStyles,
  contentPartialStyles,
  titleStyles,
}: IEcpStoreProps): JSX.Element => {
  const classes = useStyles();
  const {
    name,
    rating,
    type,
    distance,
    street,
    openinghours,
    phonenumber,
    code,
    detailedLocation,
  } = storeData;

  const phoneEl = useRef<HTMLAnchorElement>(null);
  const titleEl = useRef<HTMLHeadingElement>(null);

  const address = isVendingMachine(type) && detailedLocation?.length
    ? `${street}, ${detailedLocation}`
    : street;

  useEffect(() => {
    onScrollToStore(code, titleEl, storeCode);
  }, [storeCode]);

  const handleKeyPress = useCallback((event: KeyboardSyntheticEvent) => {
    if (phoneEl.current !== event.target
      && (event.code === 'Enter' || event.code === 'Space')) {
      event.preventDefault();
      onStoreSelect(storeData);
    }

    if (isYMEnabled() && !!code) {
      pushStoreCode(code);
    }
  }, [onStoreSelect]);

  const handleClick = useCallback((event: SyntheticEvent) => {
    if (phoneEl.current !== event.target) {
      onStoreSelect(storeData);
    }

    if (isYMEnabled() && !!code) {
      pushStoreCode(code);
    }
  }, [onStoreSelect]);

  return (
    <div
      className={classes.root}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      role="button"
      tabIndex={0}
      data-testid="store"
    >
      <span
        className={classes.arrowRight}
        data-testid="arrow-right-button"
        aria-hidden="true"
      />
      {/* Don't remove! Ya Metrica: store-title */}
      <h2
        data-testid="store-title"
        className={titleStyles || classes.title}
        ref={titleEl}
        tabIndex={-1}
      >
        {name}
      </h2>
      <div className={contentStyles || classes.content}>
        <div className={contentPartialStyles}>
          {/* Don't remove! Ya Metrica: store-rating-bar */}
          <p data-testid="store-rating-bar" className={classes.ratingWrapper}>
            {rating && (
              /* Don't remove! Ya Metrica: store-rating */
              <span
                data-testid="store-rating"
                className={cx(classes.rating, classes.pointSeparator)}
              >
                {roundRating(rating)}
              </span>
            )}
            {/* Don't remove! Ya Metrica: store-type */}
            <span
              data-testid="store-type"
              className={classes.pointSeparator}
            >
              {useStoreByType(type)}
            </span>
            {distance && (
              /* Don't remove! Ya Metrica: store-distance */
              <span data-testid="store-distance">
                {`${getRoundDistance(distance)} ${DISTANCE_UNIT}`}
              </span>
            )}
          </p>
          {/* Don't remove! Ya Metrica: store-address */}
          <p data-testid="store-address" className={cx(classes.street, classes.iconWrapper)}>
            <span className={classes.icon}>{street && <Icon type={IconTypes.mapMarkerBlack} size={IconSizes.xsm} aria-hidden="true" />}</span>
            {address}
          </p>
        </div>
        <div className={contentPartialStyles}>
          {/* Don't remove! Ya Metrica: store-hours */}
          <p
            data-testid="store-hours"
            className={cx(classes.hours, classes.iconWrapper)}
          >
            <span className={classes.icon}>{openinghours && (
              <Icon type={IconTypes.clockBlack} size={IconSizes.xs} aria-hidden="true" />
            )}
            </span>
            <span>{openinghours}</span>
          </p>
          {/* Don't remove! Ya Metrica: store-phone */}
          {phonenumber && (
            <span className={classes.iconWrapper}>
            <span className={classes.icon}>{<Icon type={IconTypes.phoneBlack} size={IconSizes.xs} aria-hidden="true" />}</span>
            <a
              ref={phoneEl}
              data-testid="store-phone"
              href={`tel:${phonenumber}`}
              className={classes.phone}
            >
              {formatPhoneNumber(phonenumber)}
            </a>
          </span>
          )}
        </div>
      </div>
    </div>
  );
};
